import Vue from 'vue';
import vuetify from '@/plugins/vuetify';
import VueI18n from 'vue-i18n';
import VueCookies from 'vue-cookies';
import VueFilterDateFormat from '@vuejs-community/vue-filter-date-format';
import messages from '@/assets/localization/messages.json';
import App from '@/App.vue';
import config from '@/config';
import router from '@/router';
import store from '@/store';
import appConfig, { AppConfig } from '@/config';
import { attachAxiosInterceptors } from '@/config/axiosInterceptors';
import * as AuthService from '@/modules/authentication/services/authenticationService';
import { doRefreshToken } from '@/modules/authentication/helpers/refreshToken';
import { fetchSsoToken } from '@/modules/authentication/helpers/ssoCodeExchange';
import { extractUrlParam } from '@/helpers/extractUrlParam';
import { renderError } from '@/helpers/renderErrorPage';
import LowerCaseFilter from '@/filters/lowercase';
import { dateUtils } from '@/mixins/dateUtils';
import { ErrorEnum } from '@/models/errorEnum';

Vue.config.productionTip = false;

// USES
Vue.use(VueI18n);
Vue.use(VueCookies);

// FILTERS
Vue.filter('lowercase', LowerCaseFilter);
Vue.use(VueFilterDateFormat);

// MIXINS
Vue.mixin(dateUtils);

// DIRECTIVES

// Load localized messages
const i18n = new VueI18n({
  locale: 'en', // set locale
  messages // set locale messages
});

async function initApp() {
  // Intercept Axios API calls and execute code: silent refresh token, set header Authorization, etc.
  attachAxiosInterceptors();

  // Check if user is already logged in
  if (Vue.$cookies.isKey(config.security.jwtRefreshCookie)) {
    await doRefreshToken();
  }

  const hash = window.location.hash.substr(1);
  const ssoCode = extractUrlParam(hash, 'code');
  if (ssoCode !== null) {
    await fetchSsoToken(ssoCode).catch(() => {
      throw new Error(ErrorEnum.INVALID_SSO_CODE);
    });
  }
  if (store.state.authentication.jwtToken === null) {
    const ssoAuthUrl = AuthService.buildSigninLink();
    window.location.replace(ssoAuthUrl);
  } else {
    new Vue({
      vuetify,
      i18n,
      router,
      store,
      render: (h) => h(App)
    }).$mount('#app');
  }
}

function isIE11(): boolean {
  // eslint-disable-next-line
  if ((window.document as any).documentMode) {
    return true;
  }
  return false;
}

function validateConfig(config: AppConfig) {
  if (
    config.api.hostname === undefined ||
    config.api.protocol === undefined ||
    config.api.privateEndpointsPrefix === undefined
  ) {
    throw new Error(ErrorEnum.INVALID_APP_CONFIG);
  }
}

try {
  if (isIE11()) {
    throw new Error(ErrorEnum.IE11);
  }
  validateConfig(appConfig);
  initApp().catch((error) => {
    renderError(error, i18n);
  });
} catch (error: unknown) {
  renderError(error as Error, i18n);
}
